import { effect, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { MdrService } from 'src/app/services/mdr.service';
import { MdrOpenRequest, MdrResponse } from 'src/app/model/mdr.model';
import { LruPageCache, MdrPaginatedListStore } from './paginated-list.store';
import { MdrSharedFilters, PAGE_CACHE_MAX_PAGES } from './list.types';

// --- Public types ----------------------------------------------------------

export interface MdrHistoryFilters extends MdrSharedFilters {
  status?: string;
  restaurant?: string;
  time_changed?: string;
}

// --- Constants -------------------------------------------------------------

/**
 * Number of pages prefetched ahead of `currentPage`. Matches the manage
 * tab's effective forward depth (BUFFER_TARGET_FACTOR=2): two pages of
 * lookahead absorb the typical "next, next, next" cadence.
 */
const PREFETCH_DEPTH = 2;

// --- Store -----------------------------------------------------------------

/**
 * Store for the "Request History" list. Read-only — no row mutations.
 *
 * Strategy: **per-page LRU cache**, symmetric for forward and backward
 * navigation. Cache hit → instant; miss → foreground HTTP + cache write.
 * A background effect keeps the next `PREFETCH_DEPTH` pages in the cache.
 *
 * The cache is cleared on every filter/sort/pageSize change (via the base's
 * `onClearVolatileState` hook) and on `refresh()`. Page numbers don't shift
 * under us — there are no row mutations in the history tab — so the cache
 * stays consistent for the lifetime of a filter/sort session.
 */
@Injectable()
export class MdrHistoryListStore extends MdrPaginatedListStore<MdrOpenRequest, MdrHistoryFilters> {
  private readonly pageCache = new LruPageCache<MdrOpenRequest[]>(PAGE_CACHE_MAX_PAGES);

  protected keyOf(item: MdrOpenRequest): string {
    return item.mdr_request_id;
  }

  constructor(private mdrService: MdrService) {
    super();

    // Forward prefetch: ensure the next PREFETCH_DEPTH pages are in cache,
    // one HTTP call at a time. Re-runs when `currentPage`/`totalItems`/
    // `pageSize` change, and when `prefetching` flips back to false
    // (chaining the next iteration).
    effect(() => {
      const page = this.currentPage();
      const total = this.totalItems();
      const size = this.pageSize();
      const isLoading = this.loading();
      const isPrefetching = this.prefetching();

      if (isLoading || isPrefetching) return;
      if (total === 0) return;

      const totalPages = Math.ceil(total / size);
      const lastToPrefetch = Math.min(page + PREFETCH_DEPTH, totalPages);

      for (let p = page + 1; p <= lastToPrefetch; p++) {
        if (!this.pageCache.has(p)) {
          this.prefetchPage(p);
          return;
        }
      }
    });
  }

  // --- Public API ----------------------------------------------------------

  /**
   * Cache-first navigation. Both forward and backward jumps are served
   * instantly when the target page is in the cache. Misses fall back to a
   * foreground HTTP load (which populates the cache via `onPageLoaded`).
   */
  override goToPage(target: number): void {
    if (target < 1 || target === this.currentPage()) return;

    const cached = this.pageCache.get(target);
    if (cached) {
      // Cancel only the prefetch (not via `cancelInflight`): bumping the
      // generation token would also kill any concurrent foreground load and
      // would force the refill effect to re-trigger immediately, causing a
      // visible `prefetching` flicker on every cache hit.
      this.cancelPrefetchOnly();
      this.displayedItems.set(cached);
      this.currentPage.set(target);
      return;
    }

    this.loadPage(target);
  }

  /** Reloads the current page from the server, discarding the cache. */
  refresh(): void {
    this.pageCache.clear();
    this.loadPage(this.currentPage());
  }

  // --- Base hooks ----------------------------------------------------------

  protected fetchPage(page: number): Observable<MdrResponse> {
    const f = this.filters();
    const { by, order } = this.sort();
    return this.mdrService.getMdrHistoryRequests(
      page,
      this.pageSize(),
      f.ships,
      f.passenger_name,
      f.date_from,
      f.date_to,
      f.cabin,
      by,
      order,
      f.status,
      f.restaurant,
      f.time_changed,
    );
  }

  protected override onPageLoaded(page: number, items: MdrOpenRequest[]): void {
    if (items.length === 0) return;
    this.pageCache.set(page, items);
  }

  protected override onClearVolatileState(): void {
    this.pageCache.clear();
  }

  // --- Private: background prefetch ----------------------------------------

  /**
   * Background prefetch: writes to the cache without touching `displayed`
   * or `currentPage`. The `loading` signal stays untouched (no spinner).
   */
  private prefetchPage(page: number): void {
    this.runPrefetch(page, items => {
      if (items.length > 0) this.pageCache.set(page, items);
    });
  }

}
